import { useId } from 'react'
import type { LocalDate } from '../../platform/localDate'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { categoryName, type CategoryMap } from './cashflowCategories'
import type { Granularity } from './cashflowPeriod'
import { periodName } from './cashflowRules'
import type { Period } from './cashflowWire'

type Props = {
  periods: Period[]
  granularity: Granularity
  currency: string
  categories: CategoryMap
  /** The server's deficit start dates (`deficitStarts`), the tile's own list. */
  deficits: ReadonlySet<LocalDate>
}

const TITLE: Record<Granularity, string> = { month: 'Month by month', quarter: 'Quarter by quarter', year: 'Year by year' }
const UNIT: Record<Granularity, string> = { month: 'Month', quarter: 'Quarter', year: 'Year' }

type Row = { period: Period; deficit: boolean }

function PeriodCell({ row, granularity }: { row: Row; granularity: Granularity }) {
  return (
    <span className="cf-month__period">
      <span className="cf-month__name">{periodName(row.period, granularity)}</span>
      {!row.period.complete && <span className="cf-month__partial">so far</span>}
    </span>
  )
}

/**
 * A stock's inherited colour with a flow's sign (`signed`), at Money's own 600:
 * `--ink`, or `--neg` on a deficit, from the class — a flow's inline `--pos`
 * would beat it, and `muted` would drop the weight the render gives the net.
 */
function Net({ row, currency }: { row: Row; currency: string }) {
  return (
    <span className={row.deficit ? 'cf-month__net cf-month__net--deficit' : 'cf-month__net'}>
      <Money cents={row.period.netCents} currency={currency} display="whole" signed />
    </span>
  )
}

/** A secondary figure: regular weight, `--ink2`, as the render draws income and expense. */
function Plain({ cents, currency }: { cents: number; currency: string }) {
  return (
    <span className="cf-month__muted">
      <Money cents={cents} currency={currency} display="whole" muted />
    </span>
  )
}

/** Out and in, stacked and each worded: two server figures, never one summed. */
function Transfers({ row, currency }: { row: Row; currency: string }) {
  return (
    <span className="cf-month__transfers">
      <span className="cf-month__transfer">
        <span className="cf-month__word">out</span>
        <Money cents={row.period.transfersOutCents} currency={currency} display="whole" muted />
      </span>
      <span className="cf-month__transfer">
        <span className="cf-month__word">in</span>
        <Money cents={row.period.transfersInCents} currency={currency} display="whole" muted />
      </span>
    </span>
  )
}

function Biggest({ row, currency, categories }: { row: Row; currency: string; categories: CategoryMap }) {
  const top = row.period.topCategory
  if (top === null) return <span className="cf-month__muted">—</span>
  return (
    <span className="cf-month__biggest">
      <span className="cf-month__muted">{categoryName(categories, top.categoryId)}</span>
      <Money cents={top.amountCents} currency={currency} display="whole" muted />
    </span>
  )
}

/**
 * "Month by month" (M3-R20): one row per period, newest first, behind a
 * closed `<details>` whose summary reads "All periods" (M3-R29): the page is
 * about one period, and the whole history is one click away, not in the way.
 * A `DataGrid` from 640px up; below it a `<ul>` of stacked cards, the `PaymentHistory`
 * precedent — `DataGrid` itself has no stacked-card renderer, and six columns
 * do not compress (M0-R28).
 *
 * Every figure is the server's per-period figure; nothing here sums. A deficit
 * is a period the server names (`deficits`, from `totals.deficit_buckets`), and
 * is the row tint AND the `−` sign AND `--neg` on the net; the in-progress
 * period says "so far", and the server never names it.
 */
export function MonthTable({ periods, granularity, currency, categories, deficits }: Props) {
  const headingId = useId()
  const phone = useBreakpoint() === 'phone'
  const title = TITLE[granularity]
  const rows: Row[] = [...periods].reverse().map((period) => ({ period, deficit: deficits.has(period.start) }))

  const columns: Column<Row>[] = [
    {
      key: 'period',
      header: UNIT[granularity],
      width: 'minmax(0, 1.6fr)',
      render: (r) => <PeriodCell row={r} granularity={granularity} />,
    },
    {
      key: 'income',
      header: 'Income',
      width: 'minmax(0, 1fr)',
      align: 'end',
      render: (r) => <Plain cents={r.period.incomeCents} currency={currency} />,
    },
    {
      key: 'expense',
      header: 'Expense',
      width: 'minmax(0, 1fr)',
      align: 'end',
      render: (r) => <Plain cents={r.period.expenseCents} currency={currency} />,
    },
    { key: 'transfers', header: 'Transfers', width: 'minmax(0, 1fr)', align: 'end', render: (r) => <Transfers row={r} currency={currency} /> },
    { key: 'net', header: 'Net', width: 'minmax(0, 1fr)', align: 'end', render: (r) => <Net row={r} currency={currency} /> },
    {
      key: 'biggest',
      header: 'Biggest category',
      width: 'minmax(0, 1.6fr)',
      align: 'end',
      render: (r) => <Biggest row={r} currency={currency} categories={categories} />,
    },
  ]

  return (
    <Panel className="cf-panel cf-month">
      <details className="cf-month__details">
        <summary className="cf-month__summary">All periods</summary>
        <section aria-labelledby={headingId} className="cf-month__section">
          <div className="cf-panel__head">
            <h2 id={headingId} className="cf-panel__title">
              {title}
            </h2>
          </div>
          {phone ? (
            <ul className="cf-month__cards" aria-label={title}>
              {rows.map((r) => (
                <li key={r.period.start} className={r.deficit ? 'cf-month__card cf-month--deficit' : 'cf-month__card'}>
                  <div className="cf-month__card-head">
                    <PeriodCell row={r} granularity={granularity} />
                    <Net row={r} currency={currency} />
                  </div>
                  <dl className="cf-month__facts">
                    <dt>Income</dt>
                    <dd>
                      <Plain cents={r.period.incomeCents} currency={currency} />
                    </dd>
                    <dt>Expense</dt>
                    <dd>
                      <Plain cents={r.period.expenseCents} currency={currency} />
                    </dd>
                    <dt>Transfers</dt>
                    <dd>
                      <Transfers row={r} currency={currency} />
                    </dd>
                    <dt>Biggest category</dt>
                    <dd>
                      <Biggest row={r} currency={currency} categories={categories} />
                    </dd>
                  </dl>
                </li>
              ))}
            </ul>
          ) : (
            <div className="cf-month__grid">
              <DataGrid
                columns={columns}
                groups={[{ label: '', rows }]}
                rowKey={(r) => r.period.start}
                label={title}
                empty={null}
                rowClassName={(r) => (r.deficit ? 'cf-month--deficit' : undefined)}
              />
            </div>
          )}
        </section>
      </details>
    </Panel>
  )
}
